/**
 * An async mutex that serializes access to the serial device.
 *
 * Any operation that communicates with the device (file read/write, filesystem listing,
 * code execution, device info queries) must acquire this mutex before proceeding.
 * This prevents race conditions where multiple operations try to use the serial port
 * simultaneously.
 *
 * Usage:
 * ```ts
 * const release = await mutex.acquire();
 * try {
 *     // ... do work with the device ...
 * } finally {
 *     release();
 * }
 * ```
 */
export class DeviceMutex {
    private locked = false;
    private queue: Array<{
        resolve: (release: () => void) => void;
        reject: (error: Error) => void;
    }> = [];

    /**
     * Acquire exclusive access to the device. If the mutex is already held,
     * this will wait until it becomes available.
     *
     * @returns A release function that MUST be called when done (ideally in a finally block).
     * @throws Error if forceRelease() is called while waiting (device disconnected).
     */
    async acquire(): Promise<() => void> {
        if (!this.locked) {
            this.locked = true;
            return this.createRelease();
        }

        return new Promise<() => void>((resolve, reject) => {
            this.queue.push({ resolve, reject });
        });
    }

    /**
     * Forcibly release the mutex and reject all queued waiters.
     *
     * Used during device disconnection to ensure nothing stays blocked.
     * After calling this, the mutex returns to an unlocked state and
     * any pending `acquire()` promises will reject with a DeviceMutexAbortedError.
     */
    forceRelease(): void {
        this.locked = false;
        const waiters = this.queue.splice(0);

        // Reject all queued waiters — the device is gone, no operation should proceed
        for (const waiter of waiters) {
            waiter.reject(
                new DeviceMutexAbortedError(
                    'DeviceMutex: force-released while waiting to acquire',
                ),
            );
        }

        this.queue = [];
    }

    private createRelease(): () => void {
        let released = false;
        return () => {
            if (released) return;
            released = true;

            if (this.queue.length > 0) {
                const next = this.queue.shift();
                if (next) next.resolve(this.createRelease());
            } else {
                this.locked = false;
            }
        };
    }

    /**
     * Whether the mutex is currently held by an operation.
     * Useful for UI state (e.g., showing a "device busy" indicator).
     */
    get isLocked(): boolean {
        return this.locked;
    }

    /**
     * Number of operations waiting to acquire the mutex.
     */
    get pendingCount(): number {
        return this.queue.length;
    }
}

/**
 * Error thrown when a queued `acquire()` is rejected due to `forceRelease()`.
 * Callers can check `instanceof DeviceMutexAbortedError` to distinguish
 * this from other errors and skip device communication gracefully.
 */
export class DeviceMutexAbortedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'DeviceMutexAbortedError';
    }
}

/**
 * Poll until the device mutex is released, then return.
 * If the mutex is not released within `maxWaitMs`, force-releases it
 * and clears the active transaction.
 *
 * Use this when you've triggered an async cleanup path (e.g., Ctrl-C → finally block)
 * and need to wait for it to naturally release the mutex before proceeding.
 *
 * @param device - The device ref containing mutex and activeTransaction
 * @param options - Configuration for polling behavior
 * @param options.maxWaitMs - Maximum time to wait before force-releasing (default 3000)
 * @param options.pollIntervalMs - Polling interval (default 50)
 * @param options.forceReleaseOnTimeout - Whether to force-release on timeout (default true)
 * @returns true if mutex released naturally, false if force-released
 */
export async function waitForMutexRelease(
    device: {
        mutex: Pick<DeviceMutex, 'isLocked' | 'forceRelease'>;
        activeTransaction: { cancel(): void } | null;
    },
    options: {
        maxWaitMs?: number;
        pollIntervalMs?: number;
        forceReleaseOnTimeout?: boolean;
    } = {},
): Promise<boolean> {
    const {
        maxWaitMs = 3000,
        pollIntervalMs = 50,
        forceReleaseOnTimeout = true,
    } = options;

    let waited = 0;
    while (device.mutex.isLocked && waited < maxWaitMs) {
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
        waited += pollIntervalMs;
    }

    if (device.mutex.isLocked && forceReleaseOnTimeout) {
        device.mutex.forceRelease();
        device.activeTransaction = null;
        return false;
    }

    return !device.mutex.isLocked;
}
